'use client';

import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react'; // Убрали useTransition
import toast from 'react-hot-toast';
import { addComment } from '../../../actions/reporterActions';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { CommentData } from '../../../types/shared';
import { useRealtime } from '../contexts/RealtimeProvider';

interface AddCommentFormProps {
  photoId: string;
  parentId?: string | null;
  onCommentAdded?: (newComment: CommentData) => void;
}

export function AddCommentForm({ photoId, parentId = null, onCommentAdded }: AddCommentFormProps) {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Свой стейт загрузки

  const { addOptimisticComment } = useRealtime();

  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return;

    setIsSubmitting(true); // Начали

    try {
      // Вызываем серверный экшен напрямую (как обычную функцию)
      const result = await addComment({ photoId, text, parentId });

      if (result.status === 'success' && result.newComment) {
        setText('');

        // 1. Мгновенно обновляем UI
        addOptimisticComment(result.newComment);

        if (onCommentAdded) {
          onCommentAdded(result.newComment);
        }
        toast.success('Комментарий добавлен!');
      } else if (result.status === 'warning' || result.status === 'banned') {
        toast.error(result.message);
      } else {
        // Безопасное извлечение сообщения об ошибке
        const msg = (result as any).message || 'Ошибка сервера';
        toast.error(msg);
      }
    } catch (error) {
      console.error(error);
      toast.error('Не удалось отправить (сеть)');
    } finally {
      setIsSubmitting(false); // Закончили
    }
  }, [text, photoId, parentId, onCommentAdded, addOptimisticComment]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-2"
    >
      <label htmlFor={`comment-${photoId}-${parentId}`} className="sr-only">
        Комментарий
      </label>
      <Textarea
        id={`comment-${photoId}-${parentId}`}
        placeholder="Ваш комментарий..."
        required
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:ring-sky-500"
        disabled={isSubmitting}
        rows={3}
      />
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={isSubmitting || !text.trim()}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isSubmitting ? 'Отправка...' : 'Отправить'}
        </Button>
      </div>
    </form>
  );
}
